/**
 * Report theme presets + the default theme, shared by the studio preview, the
 * studio download (report-doc.ts), and — mirrored in Python — the backend
 * automation delivery. Reports are authored as Markdown and rendered to HTML;
 * the resolved CSS here is what makes them look professional.
 *
 * Storage contract (meta.report.css):
 *   - empty / undefined → the built-in DEFAULT_REPORT_CSS (so the backend only
 *     needs the default for the common case).
 *   - any other string  → stored verbatim and inlined; a preset writes its full
 *     CSS here, "Advanced" lets the creator hand-edit it.
 */

/** The default report theme. Keep in sync with the Python copy in
 *  renderer/_templates_async.py and the SCSS mirror in test-panel.component.scss. */
export const DEFAULT_REPORT_CSS = `
  :root {
    --rpt-ink: #1a1d23; --rpt-muted: #6b7280; --rpt-line: #e3e6eb;
    --rpt-accent: #d81e2c; --rpt-accent-dk: #b3121f;
    --rpt-head-bg: #2b2f38; --rpt-zebra: #f6f7f9; --rpt-soft: #f3f4f6;
  }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: var(--rpt-ink); max-width: 860px; margin: 40px auto; padding: 0 28px;
         line-height: 1.62; font-size: 15px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  header { border-bottom: 3px solid var(--rpt-accent); padding-bottom: 14px; margin-bottom: 28px; }
  header h1 { font-size: 24px; margin: 0; letter-spacing: -0.01em; }
  .meta { color: var(--rpt-muted); font-size: 12.5px; margin-top: 6px; }

  /* Letterhead + document footer — the WatchGuard brand row above the title and
     the closing line. An <img> of the official logo (see WG_LOGO_PNG_B64 below)
     so the PDF engine renders identical bytes everywhere; omitted entirely (see
     resolveBrandingHtml) when the creator opts out via meta.report.hideBranding. */
  .letterhead { padding: 0 0 10px; display: flex; align-items: center; gap: 10px; }
  .letterhead .letterhead__logo { height: 22px; width: auto; display: block; }
  .letterhead .letterhead__sep { color: var(--rpt-line, #e3e6eb); }
  .letterhead .letterhead__product { color: var(--rpt-muted, #6b7280); font-weight: 600; font-size: 11px;
                                     text-transform: uppercase; letter-spacing: 0.08em; }
  .doc-foot { margin-top: 40px; padding-top: 12px; border-top: 1px solid var(--rpt-line, #e3e6eb);
              color: var(--rpt-muted, #6b7280); font-size: 11px; }

  .report { font-size: 15px; }
  .report h1, .report h2, .report h3, .report h4 { line-height: 1.25; font-weight: 650; }
  .report h1 { font-size: 21px; margin: 1.5em 0 0.5em; }
  .report h2 { font-size: 18px; margin: 1.5em 0 0.5em; padding-bottom: 5px;
               border-bottom: 1px solid var(--rpt-line); color: var(--rpt-accent-dk); }
  .report h3 { font-size: 15.5px; margin: 1.3em 0 0.4em; color: var(--rpt-ink); }
  .report h4 { font-size: 14px; margin: 1.1em 0 0.3em; color: var(--rpt-muted);
               text-transform: uppercase; letter-spacing: 0.04em; }
  .report p { margin: 0.6em 0; }
  .report ul, .report ol { margin: 0.6em 0; padding-left: 1.5em; }
  .report li { margin: 0.25em 0; }
  .report a { color: var(--rpt-accent-dk); }
  .report strong { font-weight: 680; }
  .report em { font-style: italic; }

  .report table { border-collapse: collapse; width: 100%; margin: 1em 0;
                  font-size: 13.5px; box-shadow: 0 0 0 1px var(--rpt-line); border-radius: 6px; overflow: hidden; }
  .report thead th, .report table tr:first-child th {
    background: var(--rpt-head-bg); color: #fff; font-weight: 600; text-align: left; }
  .report th, .report td { border: 1px solid var(--rpt-line); padding: 8px 12px; vertical-align: top; }
  .report tbody tr:nth-child(even), .report table tr:nth-child(even) { background: var(--rpt-zebra); }

  .report blockquote { margin: 1em 0; padding: 10px 16px; border-left: 4px solid var(--rpt-accent);
                       background: var(--rpt-soft); color: var(--rpt-ink); border-radius: 0 6px 6px 0; }
  .report code { background: var(--rpt-soft); padding: 1.5px 5px; border-radius: 4px;
                 font-size: 0.9em; font-family: "SF Mono", Consolas, monospace; }
  .report pre { background: var(--rpt-soft); padding: 12px 14px; border-radius: 8px; overflow: auto; }
  .report pre code { background: none; padding: 0; }
  .report hr { border: 0; border-top: 1px solid var(--rpt-line); margin: 1.6em 0; }

  /* Dashboard widgets — used when meta.report.format == 'dashboard'. Charts are
     inline SVG (see dashboard-doc.ts / the Python _dashboard_to_html); these
     classes style them and MUST stay in sync with the Python copy in
     renderer.py (DEFAULT_REPORT_CSS). */
  .report .dashboard-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                            gap: 16px; margin: 1em 0; }
  .report .dash-card { border: 1px solid var(--rpt-line); border-radius: 8px; padding: 14px 16px;
                       background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
  .report .dash-card h3 { margin: 0 0 10px; font-size: 14px; font-weight: 650; color: var(--rpt-ink);
                          border: 0; padding: 0; }
  .report .dash-svg { width: 100%; height: auto; font-size: 11px; }
  .report .dash-bar { fill: var(--rpt-accent); }
  .report .dash-line { fill: none; stroke: var(--rpt-accent); stroke-width: 2; }
  .report .dash-dot { fill: var(--rpt-accent); }
  .report .dash-axis { stroke: var(--rpt-line); stroke-width: 1; }
  .report .dash-lbl { fill: var(--rpt-muted); }
  .report .dash-num { fill: var(--rpt-ink); font-weight: 600; }
  .report .dash-kpi { text-align: center; padding: 8px 0; }
  .report .dash-kpi-num { font-size: 34px; font-weight: 700; color: var(--rpt-accent-dk); line-height: 1.1; }
  .report .dash-kpi-lbl { color: var(--rpt-muted); font-size: 12.5px; margin-top: 4px; }
  .report .dash-kpi-sub { color: var(--rpt-muted); font-size: 12px; margin-top: 2px; }
  .report .dash-pie-wrap { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
  .report .dash-pie { width: 150px; height: 150px; flex: 0 0 auto; }
  .report .dash-legend { font-size: 12.5px; }
  .report .dash-leg-item { margin: 3px 0; }
  .report .dash-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px;
                         margin-right: 6px; vertical-align: middle; }
  .report .dash-table { width: 100%; }
  .report .dash-empty { color: var(--rpt-muted); font-style: italic; font-size: 13px; }

  @media print {
    body { margin: 0; max-width: none; }
    .report h1, .report h2, .report h3 { break-after: avoid; }
    .report table, .report blockquote, .report pre { break-inside: avoid; }
    .report .dash-card { break-inside: avoid; }
  }`;

// Preset variations are expressed as small overrides appended to the default,
// so they inherit every base rule and only restate what changes.
const COMPACT_OVERRIDE = `
  /* Compact */
  body { font-size: 13.5px; line-height: 1.45; margin: 24px auto; }
  .report h1 { font-size: 18px; margin: 1em 0 0.3em; }
  .report h2 { font-size: 15.5px; margin: 1em 0 0.3em; }
  .report p, .report li { margin: 0.3em 0; }
  .report table { font-size: 12px; }
  .report th, .report td { padding: 5px 8px; }`;

const BRANDED_OVERRIDE = `
  /* Branded — WatchGuard red */
  header { border-bottom-width: 4px; }
  header h1 { color: var(--rpt-accent-dk); }
  .report h2 { color: #fff; background: var(--rpt-accent); border: 0; padding: 6px 12px; border-radius: 5px; }
  .report h3 { color: var(--rpt-accent-dk); }
  .report thead th, .report table tr:first-child th { background: var(--rpt-accent); }
  .report blockquote { border-left-color: var(--rpt-accent-dk); }`;

const MINIMAL_OVERRIDE = `
  /* Minimal — quiet, monochrome */
  :root { --rpt-accent: #111; --rpt-accent-dk: #111; --rpt-head-bg: #f3f4f6; }
  header { border-bottom: 1px solid #d1d5db; }
  .report h2 { color: #111; border-bottom: 0; font-weight: 700; }
  .report thead th, .report table tr:first-child th { background: #f3f4f6; color: #111; }
  .report table { box-shadow: none; border-radius: 0; }
  .report blockquote { background: none; border-left-color: #d1d5db; }`;

export type ReportThemeKey = 'default' | 'compact' | 'branded' | 'minimal';

/** Preset key → display label for the picker. */
export const REPORT_PRESETS: { key: ReportThemeKey; label: string }[] = [
  { key: 'default', label: 'Default' },
  { key: 'compact', label: 'Compact' },
  { key: 'branded', label: 'Branded' },
  { key: 'minimal', label: 'Minimal' },
];

/** The full CSS a preset stores in meta.report.css. 'default' stores empty so
 *  the built-in theme (UI + backend) stays the single source of truth. */
export function presetCss(key: ReportThemeKey): string {
  switch (key) {
    case 'compact':
      return DEFAULT_REPORT_CSS + COMPACT_OVERRIDE;
    case 'branded':
      return DEFAULT_REPORT_CSS + BRANDED_OVERRIDE;
    case 'minimal':
      return DEFAULT_REPORT_CSS + MINIMAL_OVERRIDE;
    default:
      return '';
  }
}

/** Which preset (if any) a stored css string corresponds to — drives the radio
 *  selection. Returns 'custom' when the css was hand-edited. */
export function themeKeyForCss(css: string | undefined): ReportThemeKey | 'custom' {
  const c = (css ?? '').trim();
  if (!c) return 'default';
  for (const { key } of REPORT_PRESETS) {
    if (key !== 'default' && presetCss(key).trim() === c) return key;
  }
  return 'custom';
}

/**
 * Strip the handful of CSS constructs that could break out of the inlined
 * <style> or fetch/execute external resources. Creators are trusted WG
 * employees, so this is a foot-gun guard, not a hostile-input defense:
 *   - any `<` (CSS never needs it) → blocks `</style>` breakout / HTML injection
 *   - `javascript:` URLs and IE `expression(...)`
 *   - `@import` (pulls remote stylesheets)
 */
export function sanitizeReportCss(css: string): string {
  return (css || '')
    .replace(/</g, '')
    .replace(/javascript:/gi, '')
    .replace(/expression\s*\(/gi, '')
    .replace(/@import/gi, '')
    .slice(0, 20000);
}

/** Chart/dashboard widget styling, factored out so it can be appended on top of
 *  a CUSTOM theme too. A custom/generated theme replaces DEFAULT_REPORT_CSS
 *  wholesale, which used to drop the .dash-* rules and leave inline charts
 *  unstyled in the preview and the download. The `var(--x, fallback)` defaults
 *  let it stand alone under a theme that doesn't define the design tokens.
 *  Mirror any change in renderer.py (CHART_CSS). */
export const CHART_CSS = `
  .report .dashboard-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                            gap: 16px; margin: 1em 0; }
  .report .dash-card { border: 1px solid var(--rpt-line, #e3e6eb); border-radius: 8px; padding: 14px 16px;
                       background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
  .report .dash-card h3 { margin: 0 0 10px; font-size: 14px; font-weight: 650; color: var(--rpt-ink, #1a1d23);
                          border: 0; padding: 0; }
  .report .dash-svg { width: 100%; height: auto; font-size: 11px; }
  .report .dash-bar { fill: var(--rpt-accent, #d81e2c); }
  .report .dash-line { fill: none; stroke: var(--rpt-accent, #d81e2c); stroke-width: 2; }
  .report .dash-dot { fill: var(--rpt-accent, #d81e2c); }
  .report .dash-axis { stroke: var(--rpt-line, #e3e6eb); stroke-width: 1; }
  .report .dash-lbl { fill: var(--rpt-muted, #6b7280); }
  .report .dash-num { fill: var(--rpt-ink, #1a1d23); font-weight: 600; }
  .report .dash-kpi { text-align: center; padding: 8px 0; }
  .report .dash-kpi-num { font-size: 34px; font-weight: 700; color: var(--rpt-accent-dk, #b3121f); line-height: 1.1; }
  .report .dash-kpi-lbl { color: var(--rpt-muted, #6b7280); font-size: 12.5px; margin-top: 4px; }
  .report .dash-kpi-sub { color: var(--rpt-muted, #6b7280); font-size: 12px; margin-top: 2px; }
  .report .dash-pie-wrap { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
  .report .dash-pie { width: 150px; height: 150px; flex: 0 0 auto; }
  .report .dash-legend { font-size: 12.5px; }
  .report .dash-leg-item { margin: 3px 0; }
  .report .dash-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px;
                         margin-right: 6px; vertical-align: middle; }
  .report .dash-table { width: 100%; }
  .report .dash-empty { color: var(--rpt-muted, #6b7280); font-style: italic; font-size: 13px; }

  .letterhead { padding: 0 0 10px; display: flex; align-items: center; gap: 10px; }
  .letterhead .letterhead__logo { height: 22px; width: auto; display: block; }
  .letterhead .letterhead__sep { color: var(--rpt-line, #e3e6eb); }
  .letterhead .letterhead__product { color: var(--rpt-muted, #6b7280); font-weight: 600; font-size: 11px;
                                     text-transform: uppercase; letter-spacing: 0.08em; }
  .doc-foot { margin-top: 40px; padding-top: 12px; border-top: 1px solid var(--rpt-line, #e3e6eb);
              color: var(--rpt-muted, #6b7280); font-size: 11px; }`;

/** Resolve meta.report.css to the CSS to inline: sanitized custom css, or the
 *  built-in default when empty. A custom theme always gets CHART_CSS appended so
 *  inline charts stay styled (the default already includes those rules). */
export function resolveReportCss(css: string | undefined): string {
  const c = sanitizeReportCss((css ?? '').trim());
  return c ? c + '\n' + CHART_CSS : DEFAULT_REPORT_CSS;
}


/** The official WatchGuard logo (WatchGuard_Logo_Black_Red.png in
 *  src/assets), inlined as base64 so the letterhead renders identically in the
 *  studio preview and the downloaded/emailed document — neither can rely on an
 *  external asset URL. Keep in sync with _WG_LOGO_PNG_B64 in renderer.py. */
export const WG_LOGO_PNG_B64 =
  'iVBORw0KGgoAAAANSUhEUgAAAVAAAACECAMAAADx7oNmAAAAY1BMVEVMaXEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAADtIijtIijtIijtIijtIijtIijtIijtIijtIijtIijtIijtIijtIijtIijtIigAAADtIigKMSVjAAAAH3RS' +
  'TlMAIKAwEPDQgMBAcLDgYJBQwIDwEEBgoFAgMNDgcLCQgVDy1QAAAAlwSFlzAAALEgAACxIB0t1+/AAAC1NJREFUeJztndli6ygM' +
  'hr2Dl9jtmTnbbO77P+UEJEAIbJOkZ5qp+a8a22DxgcSatCiysrKysrKysrKysrKysrKysrKysrKysrKysrKysrKeRz9fX15e/v76' +
  '20fb8Un0+8sb6MvrR5vyGWRxKv2RG+mD8nAqoh9t0Aep6RchZinlKMTQ1/dmw3FedUKvr4dxWpnkvDQ3ZxTBeY2jJ3P6vgpgWqhD' +
  'eUtOUZxXff1Vpj+hGrFJEzQuqVlt4Xx7+/YrS/BUKitHrhvFpUddY2lrb0yXlKy2cb69vfzqcjyJGouzm/vgbnkZDdTpsJXu4TwN' +
  '0AF5TWIzUC6jCaa7vf4+zpMArTtEtd/6SoHYxeYjP/7ex3mOcdOAOENX52oQaRdvxz++HeF8e/v53tY/nRpw5ekYp3561k+3kbac' +
  'gvMEHt+Au4vkgTvGh4Ff/5qA8+3Lj/e1/vlUtzc0TxQ00sq/+JrE8/t72v6MAp7jjfPKpQ2I/p7C889PH0DrkExauomn+zMB5+ef' +
  'dZaaZ9LkhwkiryP6/djb/3o/u59VQOUenkHaf45wvp5hlWm8n6chajqz/enROXAWQhGZ707eqHjRYne2B/QkOIta9++PZNC6DLaB' +
  'ngVnUSiX7W5fhye6uAH+1iTpPDjB4fW6UalXPbX8R2aytsyGVjDzHK3Tx6dJJ8JZlHbViK7Sew22acmd1m/L0BvpJwD1l3PjhMbV' +
  '6b8kwebNQC8rlTca6BHwYpt50ETPhfNKxOIbCTVvDanzgHYsudR/qNqAv749jlNYr3kOaUjX0tWyO9z6GV1YFIQaLU25+qLr9ML0' +
  'RqWrmG8Pt85koBjyL/1N+7A3ywCdpJyn/cMJmgNaMxBodFQ6M6D0nrBRtXI18/2PB509CWg9e55z4+b2TTJAO9GOvMNmIhggmTGP' +
  'PMO3lCdyT8ddnQGtmuLnX6+vr/ev0CUAvYQb3dNugkdkgLbz2I67Pq97Z9OGy7hxdWA5CbDQSDTHMdFNE3QIdImdG7h/rnckA/TS' +
  'Vl23+6Tqv90T1Dr3DPd4byhKLvSpjaQ+XDU4Aup2uisYNuszQ3efvDqUAVqUw8HeuWpVrng0JjnrwsbQ2numUTfmwaNCNUvVevEk' +
  'qn2guFezdhcyJC6HRybPB7JAj9RYGFrRcVPo8WSUasKu7ujnBLdLM20XKPK8bbPmMSUDVcNxUrHRcVPo8QSbSaFdXaHfjzDvAhT8' +
  'vXpo8eFGJQOdV2/TciHMLGcXB8jBJnPTBjPt6tPK5qz3mrYHFIy8fbPmESUDVbDI6I16t2lqpOsn41STyM5WdekVXhq0G7XY4o8O' +
  'N03TCzOmNgjQhlzWnydq3V1qepanb4a7o57bt5prpR1MEe3myTy+cE104Amkeda0q3IwR/XaCqNdLaXU7b2VWrY3vD5rnGDUHY0B' +
  '2gwYL91hgcGr0IguXtb8AnlV5YLwiE/oU5xYkYM5lXRJB9rzx+jyCPbXrqOS5G8MCKT5amI2v5rmZHan+9UXsqenJ1cYdCDQxdVg' +
  'a0qvG+he1xeEC3IhahaWW9iTHurKQBbYpjoV6IW92uuA0HddxoLOTeEmQaQLvJrw6q9P4fp1HOjArroWKrzrYA9Epb1J5h5Q/i6z' +
  'rK6BmtGYoEc6tdohEah6lTfIphwEKYBWTT/0zlaQzkeZpG+o8Vg3q6O66DmqSUdd3pgu1fhc4hhV56sf7aoRy9mWFsluBN0Dqjyq' +
  'E4syC0bXswM6Gv8T1i27q0mVbVEJQKuVLXzSXgnSuzpVwZY2VyUycBXGMGg9Y2Vb0YDVocWdB6qktUP0pXdXr325zqQER63sG3cH' +
  'u3tAi3FmZsFH9QK90yj68tqLwuu7npqYBNSV34gABd/1wyYNqEpkaqWLG9SQ1mzvB0AhCgQbWlgG6z3w3gZN3t/w3gVKReoGQyvG' +
  '1HIlBhfmVE0q0PCKkc7d79h5ECVPW1eNHCN39cOBTlGeCNRBaCxf/Qd9RdMT2bQpQLU70r0KE0l0FJqIUcP9QGk/0BcshBY8iNJe' +
  'ZjbJIzPC1tYAAwqD9LCLEaQKXBkVlACo19PZtClAC9cwpC1hYWrPK8Z0N1BqnrLB9VIwXt1ssNAJbwCVDpsPVHOKTHoEv2672fcE' +
  'Kn2gZm544bWJWdwFtCHWjQWZWuLrxo27GCAY0HqYpQ0isTlHSxpGUADKwCZ7D6CeWRaEfUSHfL/fSx02hUBpNzN5H/lAzr+L8YcC' +
  'LWe6+RwFWroi+doGKvmdEhZERSrQsvLNsiCs4fqDv+yZOrCPAKVD+4b2OvC6euMu1qgDyobGcaCbdm4D1S4SW/lMAxqaZUH4QP3A' +
  'dQtQ5nDUg3r6Ce+38btYo3bYVOOqdFepL+KR43mPAtUu0kaSJAHFAdB1zqFG9927A52DlF6ro3NNk5sbVw3ekKCxd1XnA0tCrfnu' +
  'GDHxUaA1qb7Q7mhadwFOwLSCTDjfF2gw9Sy8yY8kAUCQJKDKf1RrQgu1Y7mvMG0BrS2FiGFxoJsjmBSgYJYdYO4B9atsSQS6rOF4' +
  'gg6FSHs0L3BuLon7m1xW6KxgrOCGl1tAgcJNvTzaFzbRBKBRs7iB0dlt6rCpjjxG95J7h8xWq7tLJ/61LbYsQg/ZBMrO54cMjFwy' +
  'dNugFnygtNuyA4OF3dkAqqvMH4dWiUCDBWYlssnpukS3vtNF7prXD2j5wHhMW0C9dROiHaCYJjgQY4FqbKRUUAMikqtN4gOFJuXt' +
  'PLSpQCPdfHRTjrSi6G10kBENGfymANMtMu6y1QOWbszlN4BiHOJfibR0gIfrGqDeLVBXkmEDKHyic3noLO7YpHNF5nL28aVjWp0K' +
  'j11JtudIcazSk4LbOIYLZbxSd4GaheDRS+XowJKreQU2AOvy3Cz9nA+UL4GhK6YA7dfIam3slIuznR/F00by3GjDu2AgRnth2dgS' +
  'RTgVvqCeNcZdoJbo2gncUauX2QGFKm91G+hNt+o6Jdx/HqhZbKQEBPHg4qLe1iYC1SXniz0xp2ZJmCqXENo7LhvPQswTmmPsxXFX' +
  'J7vVg6NX8VcMx/tAzReht8w0WUrYJqzszhskm0KzGNDG5QBZzO7064GqNcnnaV4yvE08vvbLBPAWYi85Xg6feX7lMVDS8mJAa6/O' +
  'K3cWuPHMks4sPpb3H6RZHEnlGRzwCn2enwj1hRnQg2eNtznSUD6uuHiBHUysU4Cqn0HirtLNJjDV5HDGQA9Xk9prBUSvSwzo1YJ2' +
  'I4sjtTynKDLaoy7BXbLT5Vo7FhiOwarlIPuWRkC3MdpYs5ijs7KCzaXeS1DgmhKf1PWi0i7ZSTkP/skF/KkZOairF5K2pzfsW9QT' +
  '7DdWGtzAhyeXyOvjEhH0Yb9T794lS8f/5YGj5xQ5Gu8UBCjvbuBqLtF/e+LoOVVFmigfa/r3OW5whd611XOr5DFSiTXCyEqBE46U' +
  'u9xAUaqJTiz2MWb+wIq1X6A95AZqpAeG7CwG63giq61OcLhDZfJE39T6UA0hs0J4gZIl8MIrUJSRZn5eyXXlX4mlKvkgIPzamojU' +
  'yYml/fWRL8wvYc91bulu5v4uun60Qj6fto7EJEnzDPckzi15P1FY7Ej+CeGTiP+cVbrqu39C63MLiN7603fmx+8yz1BANNjdORBs' +
  'dGWeMQHR9hY4uIycecaFeMbkGTm4e+wnbrNAuLeWNifH72Yc/ATHyYVnbxJ+9N98/e32buxcMptr07ALqkec2d2P1Zs9yGoLVjnY' +
  'R3LzTJHdQW3HgQfIZnG/7bP/fxaynLz/VaP+M5X6cmQ/iFmS62NerbtFl/jJDKN2zrsdt8p9U59r2oyuWftqFiHZ/mdXBWE160ap' +
  '6Km13P8//rKysrKysrKysrKysrKysrKysrKysrKysrL+P/oX9Vqmgu0MHe4AAAAASUVORK5CYII=';

/** The letterhead + document-footer HTML to splice into the report doc shell,
 *  or a pair of empty strings when the creator opted out via
 *  meta.report.hideBranding. Mirrors the backend's _resolve_branding_html. */
export function resolveBrandingHtml(hideBranding: boolean | undefined): {
  letterhead: string;
  docFoot: string;
} {
  if (hideBranding) return { letterhead: '', docFoot: '' };
  const letterhead =
    '<div class="letterhead">' +
    `<img class="letterhead__logo" src="data:image/png;base64,${WG_LOGO_PNG_B64}" alt="WatchGuard" />` +
    '<span class="letterhead__sep">&#183;</span>' +
    '<span class="letterhead__product">AI Agents</span>' +
    '</div>';
  const docFoot = '<div class="doc-foot">Generated by WatchGuard AI Agents</div>';
  return { letterhead, docFoot };
}
